import type { DocPage } from "../types";

export const page: DocPage = {
  slug: "automations",
  title: "Scheduled tasks, Alerts, and Inbox",
  navLabel: "Scheduled and alerts",
  description:
    "Create recurring Agent work, monitor conditions with Alerts, and review items that need you in Inbox.",
  section: "automation",
  published: "2026-08-03",
  updated: "2026-08-03",
  related: ["agent", "activity", "mcp"],
  blocks: [
    {
      type: "lead",
      text: "Scheduled tasks let the Agent repeat a focused job on your timetable or when an alert fires. You choose the instructions, context, permissions, and schedule, then review each result in Run history.",
    },

    { type: "h2", text: "Create a scheduled task" },
    {
      type: "p",
      text: "Open **Scheduled** and select **New scheduled task**. Give the task a clear name and instructions that describe the result you want. An optional description can explain why the task exists, while the instructions tell the Agent what to do on every run.",
    },
    {
      type: "steps",
      items: [
        {
          title: "Set the timing",
          text: "Choose hourly, every few hours, daily, weekdays, or weekly, then confirm the timezone shown in the schedule preview.",
        },
        {
          title: "Choose its working context",
          text: "Select the model and, when useful, pin Skills or MCP servers to the task.",
        },
        {
          title: "Limit what it may change",
          text: "Reads are available automatically. Enable only the write capabilities the task needs, such as Journal, Watchlist, Alerts, or Strategy.",
        },
      ],
    },

    { type: "h2", text: "Trigger work from alerts" },
    {
      type: "p",
      text: "A scheduled task can also run when one of your Alerts fires. Create the alert first, then attach it under **Trigger on alerts** while creating or editing the task. An attached alert adds an event-driven run; it does not replace the task's regular schedule. This is useful for work such as reviewing a thesis after a price condition or market event is reached.",
    },

    { type: "h2", text: "Use Alerts and Inbox" },
    {
      type: "p",
      text: "Alerts monitor price, indicator, news, and other supported conditions independently of scheduled tasks. A fired Alert stays in Alerts history and appears in **Inbox** when it needs acknowledgement. Inbox also brings together pending approvals and account-access items that require you; open the linked item to act, then return to the originating conversation or feature for its full history.",
    },

    { type: "h2", text: "Review and control each task" },
    {
      type: "p",
      text: "The Scheduled page shows whether a task is Active or Paused, along with its next and most recent run. Open a task to see its instructions, schedule, pinned context, and **Run history**. Each run records its trigger, timing, status, and conversation. You can **Run now** for an immediate check, **Run again** after a failed run, or pause the task while you revise it.",
    },

    { type: "h2", text: "Start focused and expand deliberately" },
    {
      type: "p",
      text: "A narrow task is easier to review than a broad standing instruction. Start with one outcome, a conservative set of write capabilities, and only the context it needs. Use the pre-built tasks as editable starting points, and check the first few runs before relying on the schedule. Orders or Journal entries created by a task also appear in [Activity](/docs/activity); use Inbox for work that needs your attention.",
    },
  ],
};
